import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";

export type ReleaseSummaryInsightSeverity = "critical" | "warning" | "info" | "positive";

export interface ReleaseSummaryInsight {
  type: string;
  severity: ReleaseSummaryInsightSeverity;
  text: string;
  related_ticket_uuids: string[] | null;
}

export interface ReleaseSummaryTicket {
  id: string;
  type: string;
  key: number;
  title: string | null;
  priority: TicketPriority;
  status: TicketStatus;
}

export interface ReleaseSummary {
  app_version: string;
  headline: string;
  insights: ReleaseSummaryInsight[];
  top_open_tickets: ReleaseSummaryTicket[];
  release_health_score: number | null;
  estimated_days_until_release: number | null;
  estimated_completion_date: string | null;
  generated_at: string;
}

export interface Analytics {
  value: number;
  time: string;
  cum_value: number;
}

export interface AnalyticsData {
  data: Analytics[];
}

export interface CrashesAnalyticsPayload {
  group?: string;
  start_date: string;
  end_date: string;
  app_version?: string;
}

export interface FeedbacksAnalyticsPayload {
  group?: string;
  start_date: string;
  end_date: string;
  tag?: string;
  app_version?: string;
}

export interface UsersAnalyticsPayload {
  group?: string;
  start_date: string;
  end_date: string;
}

export interface SdkInitializationsAnalyticsPayload {
  group?: string;
  start_date: string;
  end_date: string;
}

export interface UsersFilters {
  time: TimeRange;
  group: TimeGroup;
}

export interface CrashesFilters {
  time: TimeRange;
  group: TimeGroup;
  app_version?: string;
}

export interface FeedbackFilters {
  time: TimeRange;
  group: TimeGroup;
  app_version?: string;
  tag?: string;
}

export enum TimeGroup {
  WEEK = "Week",
  DAY = "Day",
  MONTH = "Month",
}

export enum TimeRange {
  SEVEN = "Last 7 days",
  TWENTY_EIGHT = "Last 28 days",
  NINETY = "Last 90 days",
  YEAR = "Last year",
}

export interface SdkInitializationsValue {
  label: string;
  value: number;
}

export interface SdkInitializationsTimeData {
  time: string;
  values: SdkInitializationsValue[];
}

export interface SdkInitializationsAnalytics {
  data: SdkInitializationsTimeData[];
}

export type FilterType = "group" | "time" | "app_version" | "tag";

export interface ReleaseVersion {
  version: string;
  number_version: number | null;
}

export interface ReleaseVersionList {
  total: number;
  items: ReleaseVersion[];
}

export interface FeedbackVolume {
  received_feedback_tickets: number;
  closed_feedback_tickets: number;
  high_priority_tickets: number;
  closed_high_priority_tickets: number;
}

export interface CrashVolume {
  received_crash_groups: number;
  closed_crash_groups: number;
  received_crash_events: number;
}

export interface CrashFreeSessions {
  total_sessions: number;
  crash_sessions: number;
  crash_free_sessions: number;
  crash_free_rate: number | null;
  score?: number | null;
}

export interface AppHangFreeSessions {
  total_sessions: number;
  anr_sessions: number;
  anr_free_sessions: number;
  anr_free_rate: number | null;
  score?: number | null;
}

export interface NetworkHealthStatusDistribution {
  status_class: string;
  count: number;
  percentage: number;
}

export interface NetworkHealthEndpoint {
  name: string;
  method: string;
  endpoint: string;
  total_requests: number;
  score?: number | null;
  status_distribution: NetworkHealthStatusDistribution[];
}

export interface NetworkHealth {
  total: number;
  items: NetworkHealthEndpoint[];
  score?: number | null;
}

export interface NetworkSpeedEndpoint {
  name: string;
  method: string;
  endpoint: string;
  total_requests: number;
  average_duration: number;
  score?: number | null;
  is_error?: boolean;
}

export interface NetworkSpeed {
  total: number;
  items: NetworkSpeedEndpoint[];
  score?: number | null;
}

export interface ResolutionRate {
  rate: number;
  score?: number | null;
  new_points: number;
  closed_points: number;
}

export interface Mttr {
  mttr_seconds: number;
  mttr_hours: number;
  mttr_days: number;
  resolved_count: number;
  score?: number | null;
}

export interface TestingCoverageItem {
  value: string;
  count: number;
  percentage: number;
  minimum_sessions?: number | null;
}

export interface TestingCoverageDimension {
  score?: number | null;
  items: TestingCoverageItem[];
}

export interface TestingCoverage {
  tickets: Record<string, TestingCoverageItem[]>;
  sessions: {
    dimensions: Record<string, TestingCoverageDimension>;
    metadata: Record<string, TestingCoverageDimension>;
  };
  score?: number | null;
}

export interface CurrentDuration {
  first_ticket_at: string | null;
  last_ticket_at: string | null;
  duration_days: number;
}

export interface ReleaseDateEstimate {
  open_issues: number;
  open_crashes: number;
  open_items: number;
  mttr_days: number;
  estimated_days_until_release: number;
}

export interface ScoringConfig {
  metric: string;
  source: string;
  config: {
    anchors?: [number, number][];
    enabled_dimensions?: Record<string, boolean>;
    tracked_metadata_keys?: string[];
    dimension_targets?: Record<string, { value: string; minimum_sessions: number }[]>;
    metadata_targets?: Record<string, { value: string; minimum_sessions: number }[]>;
    min_weight?: number;
    mean_weight?: number;
    tx_3xx_weight?: number;
    tx_4xx_weight?: number;
    tx_5xx_severity?: number;
    [key: string]: unknown;
  };
}

export interface ReleaseHealthOverview {
  feedback_volume: FeedbackVolume;
  crash_volume: CrashVolume;
  crash_free_sessions: CrashFreeSessions;
  anr_free_sessions: AppHangFreeSessions;
  network_health: NetworkHealth;
  network_speed: NetworkSpeed;
  resolution_rate: ResolutionRate;
  mttr: Mttr;
  testing_coverage: TestingCoverage;
  current_duration: CurrentDuration;
  release_date_estimate: ReleaseDateEstimate;
  release_health_score?: number | null;
  release_summary?: ReleaseSummary | null;
}

export interface MetadataList {
  metadata_keys: string[];
}

export interface SearchValueHit {
  value: string;
  count: number;
}

export interface SearchValues {
  key: string;
  values: SearchValueHit[];
}
